"use client";

import { ComponentWithAnalyticsGlobalField } from "@solarwinds-webbiz/types";
import NextLink, { LinkProps as NextLinkProps } from "next/link";
import { forwardRef } from "react";
import {
  getDataAutomationGroup,
  getDataLinkAttributes,
  handleClickScroll,
  handleSpecialLinks,
  isHrefFragment,
} from "@/helpers";
import {
  isAbsoluteUrl,
  isInternalUrl,
  replaceAbsoluteUrlWithRelative,
} from "./Link.helpers";

export interface LinkProps
  extends
    Omit<React.ComponentPropsWithoutRef<"a">, "href">,
    Omit<NextLinkProps, "passHref">,
    ComponentWithAnalyticsGlobalField {}

/**
 * Component that renders `next/link` for relative URLs, enabling client-side transitions between routes.
 * Renders `<a>` element for absolute URLs, preserving default browser behavior.
 */
const Link = forwardRef<HTMLAnchorElement, LinkProps>(
  (
    {
      href,
      replace,
      scroll,
      shallow,
      prefetch = false,
      dld,
      dlt,
      dhd,
      ...delegated
    },
    ref,
  ) => {
    const dataLinkAttributes = getDataLinkAttributes({ dlt, dld, dhd });

    const resolvedHref =
      typeof href === "string" ? replaceAbsoluteUrlWithRelative(href) : href;

    if (typeof resolvedHref === "string" && isAbsoluteUrl(resolvedHref)) {
      return (
        <a
          ref={ref}
          href={resolvedHref}
          {...dataLinkAttributes}
          {...(isInternalUrl(resolvedHref)
            ? {}
            : { target: "_blank", rel: "noopener noreferrer" })}
          {...getDataAutomationGroup("link")}
          {...delegated}
        />
      );
    }

    // TODO: after hotfix, look into removing double isHrefFragment() call
    if (typeof resolvedHref === "string" && isHrefFragment(resolvedHref)) {
      return (
        <a
          href={resolvedHref}
          onClick={(e) => {
            if (handleSpecialLinks(e, resolvedHref)) return;
            if (isHrefFragment(resolvedHref)) {
              handleClickScroll(e, resolvedHref);
            }
          }}
          {...getDataAutomationGroup("link")}
          {...dataLinkAttributes}
          {...delegated}
        />
      );
    }

    return (
      <NextLink
        href={resolvedHref || "#"}
        replace={replace}
        scroll={scroll}
        shallow={shallow}
        prefetch={prefetch}
        ref={ref}
        onNavigate={(event) => {
          event.preventDefault();
          location.href = resolvedHref.toString();
        }}
        {...dataLinkAttributes}
        {...delegated}
        {...getDataAutomationGroup("link")}
      />
    );
  },
);

Link.displayName = "Link";

export default Link;
